import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import {
  collection,
  getDocs,
  addDoc,
  query,
  where,
  updateDoc,
  doc,
  getDoc
} from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import '../style/common.css';
import './PMDashboard.css';
import { useNavigate } from 'react-router-dom';

function PMDashboard() {
  const [assignedPOs, setAssignedPOs] = useState([]);
  const [raCounts, setRaCounts] = useState({});
  const [raSums, setRaSums] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeFormPO, setActiveFormPO] = useState(null);
  const [billName, setBillName] = useState('');
  const [billAmount, setBillAmount] = useState('');
  const [billNumber, setBillNumber] = useState('');
  const [billDate, setBillDate] = useState('');
  const [userName, setUserName] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPO, setFilterPO] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [totalRAAmountSinceApril, setTotalRAAmountSinceApril] = useState(0);
  const [totals, setTotals] = useState({
    totalPOAmount: 0,
    totalRASubmitted: 0,
    totalBalance: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserAndPOs = async () => {
      setLoading(true);

      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        setUserName(userSnap.data().name || userSnap.data().email);
      }

      const poSnap = await getDocs(
        query(collection(db, 'purchase_orders'),
          where('assigned_to', '==', auth.currentUser.uid)
        )
      );
      const poList = poSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAssignedPOs(poList);

      const billsSnap = await getDocs(collection(db, 'ra_bills'));
      const counts = {};
      const sums = {};

      let totalSinceApril = 0;
      const now = new Date();
      const financialYearStart = new Date(now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear(), 3, 1);

      billsSnap.docs.forEach(d => {
        const b = d.data();
        if (b.po_id) {
          counts[b.po_id] = (counts[b.po_id] || 0) + 1;
          const amt = parseFloat(b.amount || 0);
          sums[b.po_id] = (sums[b.po_id] || 0) + amt;

          const billDate = new Date(b.submitted_at);
          if (billDate >= financialYearStart) {
            totalSinceApril += amt;
          }
        }
      });

      let totalPOAmount = 0;
      let totalRASubmitted = 0;

      poList.forEach(po => {
        const submitted = sums[po.id] || 0;
        totalPOAmount += po.po_total_amount || 0;
        totalRASubmitted += submitted;
      });

      setRaCounts(counts);
      setRaSums(sums);
      setTotalRAAmountSinceApril(totalSinceApril);
      setTotals({
        totalPOAmount,
        totalRASubmitted,
        totalBalance: totalPOAmount - totalRASubmitted,
      });

      setLoading(false);
    };

    fetchUserAndPOs();
  }, []);

  const markCompleted = async (poId) => {
    if (!window.confirm('Mark this PO as completed?')) return;
    await updateDoc(doc(db, 'purchase_orders', poId), {
      completed: true,
      balance_amount: 0
    });
    setAssignedPOs(prev =>
      prev.map(po => po.id === poId ? { ...po, completed: true, balance_amount: 0 } : po)
    );
  };

  const openForm = (poId) => {
    setActiveFormPO(activeFormPO === poId ? null : poId);
    setBillName('');
    setBillAmount('');
    setBillNumber('');
    setBillDate('');
  };

  const submitRA = async (poId) => {
    if (!billAmount || !billName.trim() || !billNumber.trim() || !billDate) {
      return alert('All fields are required');
    }

    const count = (raCounts[poId] || 0) + 1;
    const amount = parseFloat(billAmount);

    await addDoc(collection(db, 'ra_bills'), {
      po_id: poId,
      amount,
      submitted_by: auth.currentUser.uid,
      submitted_at: new Date().toISOString(),
      bill_name: billName.trim(),
      bill_number: billNumber.trim(),
      bill_date: billDate
    });

    setRaCounts(prev => ({ ...prev, [poId]: count }));
    setRaSums(prev => ({ ...prev, [poId]: (prev[poId] || 0) + amount }));
    setBillName('');
    setBillAmount('');
    setBillNumber('');
    setBillDate('');
    alert(`${billName.trim()} submitted`);
    setActiveFormPO(null);
  };

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = '/';
  };

  const filteredPOs = assignedPOs.filter(po => {
    const jcMatch = po.jc_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const poMatch = po.po_number?.toLowerCase().includes(filterPO.toLowerCase());
    const dateMatch =
      (!fromDate || new Date(po.po_date) >= new Date(fromDate)) &&
      (!toDate || new Date(po.po_date) <= new Date(toDate));
    return (showCompleted ? po.completed : !po.completed) && jcMatch && poMatch && dateMatch;
  });

  const renderPOList = (poList, isCompleted = false) => (
    <div className="po-list">
      {poList.map(po => {
        const submitted = raSums[po.id] || 0;
        const balance = Math.max((po.po_total_amount || 0) - submitted, 0);

        return (
          <div className="po-card" key={po.id}>
            <h4>{po.po_number} | JC: {po.jc_number}</h4>
            <p><strong>Location:</strong> {po.location}</p>
            <p><strong>Description:</strong> {po.description || '-'}</p>
            <p><strong>Total Amount:</strong> ₹{po.po_total_amount}</p>
            <p><strong>Submitted:</strong> ₹{submitted.toFixed(2)}</p>
            <p><strong>Balance Left:</strong> ₹{balance.toFixed(2)}</p>
            <p><strong>Date:</strong> {po.po_date}</p>
            <p>
              <strong>Status:</strong>{' '}
              <span className={po.completed ? 'status-completed' : 'status-active'}>
                {po.completed ? 'Completed' : 'Active'}
              </span>
            </p>

            {!isCompleted && (
              <div className="po-actions">
                <button onClick={() => markCompleted(po.id)}>Mark as Completed</button>
                <button onClick={() => openForm(po.id)}>
                  {activeFormPO === po.id ? 'Cancel' : `Submit RA (${(raCounts[po.id] || 0) + 1})`}
                </button>
              </div>
            )}

            {activeFormPO === po.id && (
              <div className="ra-form">
                <div className="form-group">
                  <label>RA Bill Name</label>
                  <input
                    type="text"
                    value={billName}
                    onChange={e => setBillName(e.target.value)}
                    placeholder={`RA ${(raCounts[po.id] || 0) + 1}`}
                  />
                </div>
                <div className="form-group">
                  <label>Invoice Number</label>
                  <input
                    type="text"
                    value={billNumber}
                    onChange={e => setBillNumber(e.target.value)}
                    placeholder="e.g., INV-001"
                  />
                </div>
                <div className="form-group">
                  <label>Bill Date</label>
                  <input
                    type="date"
                    value={billDate}
                    onChange={e => setBillDate(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>RA Bill Amount</label>
                  <input
                    type="number"
                    value={billAmount}
                    onChange={e => setBillAmount(e.target.value)}
                  />
                </div>
                <button onClick={() => submitRA(po.id)}>Submit</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="pm-dashboard">
      <div className="pm-navbar">
        <h3>Hi, {userName}</h3>
        <button className="pm-logout-btn" onClick={handleLogout}>Logout</button>
      </div>

      <div className="pm-content">
        <div className="summary-box">
          <h4>Summary</h4>
          <p><strong>Total PO Amount:</strong> ₹{totals.totalPOAmount.toFixed(2)}</p>
          <p><strong>Total RA Submitted:</strong> ₹{totals.totalRASubmitted.toFixed(2)}</p>
          <p><strong>Pending Balance:</strong> ₹{totals.totalBalance.toFixed(2)}</p>
          <p><strong>RA Bills Since April 1st:</strong> ₹{totalRAAmountSinceApril.toFixed(2)}</p>
        </div>

        <h2>Your Assigned Purchase Orders</h2>

        {loading ? (
          <p>Loading POs...</p>
        ) : (
          <>
            <div className="pm-controls">
              <button
                className={`tab-btn ${!showCompleted ? 'active' : ''}`}
                onClick={() => setShowCompleted(false)}
              >
                Running POs
              </button>
              <button
                className={`tab-btn ${showCompleted ? 'active' : ''}`}
                onClick={() => setShowCompleted(true)}
              >
                Completed POs
              </button>

              <input
                type="text"
                placeholder="Search JC Number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-bar"
              />
              <input
                type="text"
                placeholder="Search PO Number"
                value={filterPO}
                onChange={(e) => setFilterPO(e.target.value)}
                className="search-bar"
              />
              <div className="date-filter">
                <label>From Date:</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="search-bar"
                />
              </div>
              <div className="date-filter">
                <label>To Date:</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="search-bar"
                />
              </div>
            </div>

            {filteredPOs.length === 0 ? (
              <p>No POs match your search.</p>
            ) : (
              renderPOList(filteredPOs, showCompleted)
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PMDashboard;
